import apiService from './apiService';
import { API_ENDPOINTS } from './apiEndpoints';

const EXCLUDED_METHOD_TYPES = new Set(['upi']);
const SUPPORTED_METHOD_TYPES = new Set(['bank_transfer', 'cheque', 'cash']);

const TYPE_ALIASES = {
  upi: null,
  upi_online_transfer: null,
  'upi_/_online_transfer': null,
  online_transfer: null,
  'bank transfer': 'bank_transfer',
  bank_transfer: 'bank_transfer',
  banktransfer: 'bank_transfer',
  cheque: 'cheque',
  check: 'cheque',
  cash: 'cash',
};

/** Default forms per type when API lists methods without field definitions (matches mobile app). */
const TYPE_FORM_TEMPLATES = {
  bank_transfer: {
    title: 'Bank Transfer Details',
    fields: [
      {
        key: 'transaction_reference_no',
        label: 'Transaction Reference No.',
        required: true,
        maxLength: 22,
      },
      {
        key: 'sender_bank_name',
        label: 'Sender Bank Name',
        required: false,
        maxLength: 50,
      },
    ],
    fileField: {
      key: 'payment_confirmation',
      label: 'Payment Confirmation Screenshot/PDF',
      required: true,
      accept: 'image/*,.pdf,application/pdf',
    },
  },
  cheque: {
    title: 'Cheque Details',
    fields: [
      { key: 'cheque_number', label: 'Cheque Number', required: true, maxLength: 6 },
      {
        key: 'bank_name_branch',
        label: 'Bank Name',
        required: false,
        maxLength: 50,
      },
      { key: 'cheque_date', label: 'Cheque Date', required: true, type: 'date' },
      {
        key: 'cheque_amount',
        label: 'Cheque Amount',
        required: true,
        type: 'decimal',
        readOnlyWhenRemaining: true,
      },
    ],
    fileField: {
      key: 'cheque_image',
      label: 'Cheque Image (Front & Back)',
      required: true,
      accept: 'image/*',
    },
  },
  cash: {
    title: 'Cash Payment Details',
    fields: [
      {
        key: 'receiver_name',
        label: 'Receiver Name (Staff)',
        required: true,
        maxLength: 50,
      },
    ],
    fileField: null,
  },
};

const FILE_FIELD_KEY_MAP = {
  payment_confirmation: 'payment_confirmation',
  payment_confirmation_file: 'payment_confirmation',
  payment_screenshot: 'payment_screenshot',
  cheque_image: 'cheque_image',
};

const FILE_ACCEPT_BY_KEY = {
  payment_confirmation: 'image/*,.pdf,application/pdf',
  payment_screenshot: 'image/*',
  cheque_image: 'image/*',
};

const normalizeMethodName = (name = '') =>
  String(name)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_');

const normalizeMethodType = (method = {}) => {
  const nameKey = normalizeMethodName(method.name || '');
  const typeKey = normalizeMethodName(
    method.type || method.method_type || method.payment_type || ''
  );
  const raw = typeKey || nameKey;

  if (!raw) return null;
  if (EXCLUDED_METHOD_TYPES.has(raw) || raw.includes('upi') || raw.includes('online_transfer')) {
    return null;
  }
  if (nameKey.includes('upi') || nameKey.includes('online_transfer')) {
    return null;
  }

  if (TYPE_ALIASES[raw] === null) return null;
  if (TYPE_ALIASES[raw]) return TYPE_ALIASES[raw];
  if (TYPE_ALIASES[nameKey]) return TYPE_ALIASES[nameKey];
  return SUPPORTED_METHOD_TYPES.has(raw) ? raw : null;
};

const getTypeFormTemplate = (type) => {
  const template = TYPE_FORM_TEMPLATES[type];
  if (!template) return null;
  return {
    fields: (template.fields || []).map((field) => ({ ...field })),
    fileField: template.fileField ? { ...template.fileField } : null,
    title: template.title,
  };
};

const objectToFieldsArray = (obj = {}) =>
  Object.entries(obj).map(([key, cfg]) => ({
    field_name: key,
    key,
    ...(typeof cfg === 'object' && cfg !== null ? cfg : { label: String(cfg) }),
  }));

const coerceFieldsArray = (method) => {
  const candidates = [
    method.fields,
    method.form_fields,
    method.payment_fields,
    method.payment_method_fields,
    method.field_config,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate;
    }
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      const fromObject = objectToFieldsArray(candidate);
      if (fromObject.length > 0) return fromObject;
    }
    if (typeof candidate === 'string') {
      try {
        const parsed = JSON.parse(candidate);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        if (parsed && typeof parsed === 'object') {
          const fromObject = objectToFieldsArray(parsed);
          if (fromObject.length > 0) return fromObject;
        }
      } catch {
        /* ignore */
      }
    }
  }
  return [];
};

const mapFileFieldKey = (key = '') => FILE_FIELD_KEY_MAP[key] || key;

const isFileFieldDefinition = (field = {}) => {
  const fieldType = String(field.field_type || field.type || '').toLowerCase();
  const key = String(field.field_name || field.field_key || field.key || field.name || '');
  return (
    fieldType === 'file' ||
    fieldType === 'image' ||
    /confirmation|screenshot|cheque_image|payment_file|upload/i.test(key)
  );
};

const buildFormFromFieldDefinitions = (rawFields) => {
  const fields = [];
  let fileField = null;

  rawFields.forEach((field) => {
    const key = field.field_name || field.field_key || field.key || field.name;
    if (!key) return;

    if (isFileFieldDefinition(field)) {
      const mappedKey = mapFileFieldKey(key);
      fileField = {
        key: mappedKey,
        apiKey: key,
        label: field.label || field.display_name || field.field_label || key,
        required: field.required === true || field.is_required === true,
        accept: FILE_ACCEPT_BY_KEY[mappedKey] || 'image/*,.pdf,application/pdf',
      };
      return;
    }

    const fieldType = String(field.field_type || field.type || 'text').toLowerCase();

    fields.push({
      key,
      label: field.label || field.display_name || field.field_label || key,
      required: field.required === true || field.is_required === true,
      maxLength: field.max_length ?? field.maxLength ?? undefined,
      type:
        fieldType === 'date'
          ? 'date'
          : fieldType === 'number' || fieldType === 'decimal'
            ? 'decimal'
            : 'text',
      readOnlyWhenRemaining: key === 'cheque_amount',
    });
  });

  return { fields, fileField };
};

export const parseAdvancePaymentMethod = (method = {}) => {
  if (method.is_deleted) return null;

  const type = normalizeMethodType(method);
  if (!type) return null;

  const apiFields = coerceFieldsArray(method);
  const template = apiFields.length > 0 ? null : getTypeFormTemplate(type);
  if (apiFields.length === 0 && !template) return null;

  const { fields, fileField } =
    apiFields.length > 0
      ? buildFormFromFieldDefinitions(apiFields)
      : {
          fields: template.fields,
          fileField: template.fileField,
        };

  if (fields.length === 0 && !fileField) return null;

  return {
    id: String(method.id),
    value: String(method.id),
    type,
    label: method.name || method.label || type.replace(/_/g, ' '),
    title: method.title || template?.title || `${method.name || 'Payment'} Details`,
    fields,
    fileField,
    fieldsFromApi: apiFields.length > 0,
  };
};

export const fetchAdvancePaymentMethods = async () => {
  const userId = localStorage.getItem('user_id') || '';
  const response = await apiService.get(
    `${API_ENDPOINTS.GET_advance_payment_methods}?user_id=${encodeURIComponent(userId)}`
  );

  const list =
    response?.data?.AdvancePaymentMethod ||
    response?.data?.advance_payment_method ||
    response?.AdvancePaymentMethod ||
    [];

  if (!Array.isArray(list)) return [];

  return list
    .filter((method) => !method.is_deleted)
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
    .map(parseAdvancePaymentMethod)
    .filter(Boolean);
};

export const buildPaymentMethodsById = (methods = []) =>
  methods.reduce((acc, method) => {
    acc[method.value] = method;
    return acc;
  }, {});

export default {
  fetchAdvancePaymentMethods,
  parseAdvancePaymentMethod,
  buildPaymentMethodsById,
};
